import axios from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";
import portalAxios from "../portalAxios";
import { PaginatedPortalPaymentResult } from "../contracts/PaginatedPortalPaymentResult";
import { PortalDebitAttemptResponse } from "../contracts/PortalDebitAttemptResponse";
import { PortalPaymentResponseMapper } from "../mappers/PortalPaymentResponseMapper";
import { PortalPayment, PortalPendingDebit } from "../models/PortalPayment";
import { contentDispositionFileName } from "../../training/utils/contentDispositionFileName";

const mapper = new PortalPaymentResponseMapper();

/** Rows per "Show more". */
export const PORTAL_PAYMENTS_PAGE_SIZE = 10;

/** Why the API refused a pay request (409 `reason`), or `failed` for anything else. */
export type PortalPayFailure = "not-payable" | "pending" | "mandate-missing" | "failed";

export interface PortalPaymentsPage {
  memberId: string;
  page: number;
  totalPages: number;
  items: PortalPayment[];
}

const paymentsPath = (memberId: string) => `/portal/members/${memberId}/payments`;

/** One page of the target member's Payments, newest first; page 1 replaces, later pages append. */
export const fetchPortalPayments = createAsyncThunk<
  PortalPaymentsPage,
  { memberId: string; page?: number },
  { rejectValue: string }
>("portalMoney/fetchPayments", async ({ memberId, page = 1 }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<PaginatedPortalPaymentResult>(paymentsPath(memberId), {
      params: { page, pageSize: PORTAL_PAYMENTS_PAGE_SIZE },
    });
    return {
      memberId,
      page: response.data.page,
      totalPages: response.data.totalPages,
      items: mapper.toItems(response.data.items),
    };
  } catch {
    return rejectWithValue("failed");
  }
});

/** Starts the SEPA debit for one Payment through the member's existing mandate (PD-0035). */
export const payPortalPayment = createAsyncThunk<
  { memberId: string; paymentId: string; debit: PortalPendingDebit },
  { memberId: string; paymentId: string },
  { rejectValue: PortalPayFailure }
>("portalMoney/pay", async ({ memberId, paymentId }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.post<PortalDebitAttemptResponse>(
      `${paymentsPath(memberId)}/${paymentId}/pay`,
    );
    return { memberId, paymentId, debit: mapper.toPendingDebit(response.data) };
  } catch (error) {
    return rejectWithValue(toPayFailure(error));
  }
});

/**
 * Fetches the issued invoice or receipt as a blob (the portal bearer header is needed, so never a
 * bare link to the API) and offers it under the server's file name.
 */
export const downloadPortalDocument = createAsyncThunk<
  string,
  { memberId: string; paymentId: string },
  { rejectValue: string }
>("portalMoney/downloadDocument", async ({ memberId, paymentId }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<Blob>(`${paymentsPath(memberId)}/${paymentId}/document`, {
      responseType: "blob",
    });
    const fileName =
      contentDispositionFileName(response.headers?.["content-disposition"] as string | undefined) ?? "document.pdf";
    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return fileName;
  } catch {
    return rejectWithValue("failed");
  }
});

const toPayFailure = (error: unknown): PortalPayFailure => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return "failed";
  const reason = (error.response.data as { reason?: unknown } | undefined)?.reason;
  return reason === "not-payable" || reason === "pending" || reason === "mandate-missing" ? reason : "failed";
};
